import { CalendarDate, createCalendar, getLocalTimeZone, today } from '@internationalized/date';
import { useDateField, useDateSegment } from '@react-aria/datepicker';
import { useLocale } from '@react-aria/i18n';
import { useDateFieldState } from '@react-stately/datepicker';
import React from 'react';

import './index.css';
import type { DateFieldProps } from '@react-aria/datepicker';
import type { DateFieldState, DateSegment } from '@react-stately/datepicker';
import type { IFieldProps } from 'bz-core-controls/controls/form/field/field/IFieldProps';
import classNames from 'classnames';
import { getDateTimeFormat } from '../field-date-picker-react-aria/utils';

export interface IFieldDOBProps extends IFieldProps {
  value?: string | null;
  errorText?: string;
  required?: boolean;
  containerClass?: string;
  onChange(dateString: string): void;
}

const BuildCalendarDate = (dateString: string | null) => {
  if (!dateString) return null;
  const [year, month, day, hours, min, secs] = dateString.split(/T|:|-/);
  return new CalendarDate(parseInt(year), parseInt(month), parseInt(day));
};

const FieldDOB = (props: IFieldDOBProps) => {
  const rgx = /^[0-9]{4}$/;
  const { errorText, submitted } = props;
  const ref = React.useRef(null);

  const val = props.value ? BuildCalendarDate(props.value) : null;

  let [value, setValue] = React.useState(val);
  return (
    <div className={`field-container ${props.containerClass}`}>
      <span className="grid grid-cols-[auto_auto] justify-start">
        <label className="click-label">{props.label}</label>
        {props.required && <span className="text-normal pl-px text-rose-600">*</span>}
      </span>
      <div ref={ref}>
        <DateField
          maxValue={today(getLocalTimeZone())}
          value={value}
          granularity="day"
          onChange={(e) => {
            // prevent onChange until 4 digit year is entered
            if (rgx.test(e.year)) {
              const dateString = new CalendarDate(e.year, e.month, e.day).toString();
              props.onChange(dateString + ' 00:00:00.000');
            }
            setValue(e);
          }}
        />
      </div>
      {!!errorText && submitted && (
        <div className="pb-2 text-xs font-medium text-rose-500">
          <p>{errorText}</p>
        </div>
      )}
    </div>
  );
};

const DateField = (props: DateFieldProps<CalendarDate>) => {
  const { locale } = useLocale();
  const state = useDateFieldState({
    ...props,
    locale: 'en-GB',
    createCalendar
  });

  const ref = React.useRef(null);

  const { labelProps, fieldProps } = useDateField(props, state, ref);

  return (
    <div className="wrapper">
      <span {...labelProps}>{props.label}</span>
      <div {...fieldProps} ref={ref} className="field flex">
        {state.segments
          .filter((s) => s.type !== 'literal')
          .map((segment, i) => (
            <DateSeg key={i} segment={segment} state={state} />
          ))}
      </div>
    </div>
  );
};

interface DateSegmentProps {
  segment: DateSegment;
  state: DateFieldState;
}

const DateSeg = ({ segment, state, ...otherProps }: DateSegmentProps) => {
  const ref = React.useRef(null);
  const { segmentProps } = useDateSegment(segment, state, ref);

  const className = classNames(
    'click-input click-input-light mb-1 w-1/3 flex bg-white justify-between pl-2',
    {
      day: segment.type === 'day',
      month: segment.type === 'month',
      year: segment.type === 'year',
      'invalid-class': state.validationState === 'invalid'
    }
  );

  return (
    <div className={className}>
      <div
        {...segmentProps}
        className={classNames('datePicker-cell', {
          'is-placeholder': segment.isPlaceholder,
          'is-read-only': !segment.isEditable
        })}
        style={{
          minWidth: !state.value ? '3ch' : (null as any)
        }}
        data-testid={`${segment.type}-${otherProps.id}`}
        ref={ref}
      >
        {segment.isPlaceholder ? getDateTimeFormat(segment.type) : segment.text}
      </div>
    </div>
  );
};

FieldDOB.defaultProps = {
  containerClass: ''
};

export default FieldDOB;
